// SeriesDebrief.tsx - the session/series debrief (S25), a pure function of a SeriesDebrief
// (GET /api/progress/debrief/:sessionId?cadence=). Reference lane #4: Chess.com end-of-game report.
// Surfaces jump-to-worst / jump-to-best (the item's headline feature), the run broken into units at the
// chosen cadence, and a run-average footer. The cadence pill reflects the data's ACTUAL cadence.
import type { SeriesDebrief as Debrief, DecisionRef, CoachingCadence } from '@postflop/schema';
import { toneVar, fmt1, barPct } from './grade';

const CADENCES: { id: CoachingCadence; label: string }[] = [
  { id: 'perHand', label: 'Per hand' },
  { id: 'perSeries', label: 'Per series' },
  { id: 'perSession', label: 'Per session' },
];
const UNIT_NOUN: Record<string, string> = { decision: 'Decision', hand: 'Hand', series: 'Series', session: 'Session' };

const capStreet = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

export interface SeriesDebriefProps {
  data: Debrief;
  onCadence: (c: CoachingCadence) => void;
  /** Optional: jump to a decision in the replay timeline (S26). Absent => the card is a visual cue only. */
  onJump?: (ref: DecisionRef) => void;
}

export function SeriesDebrief({ data, onCadence, onJump }: SeriesDebriefProps) {
  const { units, overall } = data;
  // handId -> "Hand N" in play order, so jump cards can name a place instead of a raw ULID.
  const handLabel = new Map<string, string>();
  let n = 0;
  for (const u of units) for (const h of u.handIds) if (!handLabel.has(h)) handLabel.set(h, `Hand ${++n}`);
  const where = (r: DecisionRef): string =>
    `${handLabel.get(r.handId) ?? 'Hand'} . ${r.street}`;

  const jumpCard = (kind: 'worst' | 'best', ref: DecisionRef | undefined) => {
    if (!ref) return null;
    const label = kind === 'worst' ? 'Your worst decision' : 'Your best decision';
    return (
      <button type="button" className={`jcard ${kind}`} onClick={() => onJump?.(ref)}>
        <div className="jhead">
          <span className="jl">{label}</span>
          <span className="g num">{ref.grade}</span>
        </div>
        <div className="jbody">{ref.note ?? capStreet(ref.street)}</div>
        <div className="jfoot">
          <span className="where">{where(ref)}</span>
          <span className="go">Jump to it &gt;</span>
        </div>
      </button>
    );
  };

  return (
    <div className="prog">
      <div>
        <div className="kicker">Session debrief</div>
        <div className="h2">This session, hand by hand</div>
        <div className="lede num">
          {overall.handCount} hands . {overall.decisionCount} decisions . avg {fmt1(overall.avgGrade)} of 10
        </div>
      </div>

      <div className="cadence" role="tablist">
        {CADENCES.map((c) => (
          <button
            key={c.id}
            type="button"
            role="tab"
            aria-selected={data.cadence === c.id}
            className={data.cadence === c.id ? 'on' : ''}
            onClick={() => onCadence(c.id)}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="slab">Jump straight to the moments that mattered</div>
      <div className="jump">
        {jumpCard('worst', overall.worst)}
        {jumpCard('best', overall.best)}
      </div>

      <div className="slab">Every {UNIT_NOUN[units[0]?.scope ?? 'hand'].toLowerCase()} this run</div>
      <div className="jump">
        {units.map((u, i) => {
          const noun = UNIT_NOUN[u.scope] ?? 'Unit';
          const note = u.worst?.note ?? u.best?.note ?? '';
          return (
            <button
              type="button"
              key={u.key}
              className="hrow"
              onClick={() => (u.worst || u.best) && onJump?.((u.worst ?? u.best) as DecisionRef)}
            >
              <span className="hn">
                {noun} {i + 1}
              </span>
              <div className="hmid">
                <div className="hbar">
                  <span style={{ width: `${barPct(u.avgGrade)}%`, background: toneVar(u.avgGrade) }} />
                </div>
                {note && <div className="hnote">{note}</div>}
              </div>
              <span className="hg num" style={{ color: toneVar(u.avgGrade) }}>
                {fmt1(u.avgGrade)}
              </span>
            </button>
          );
        })}
      </div>

      <div className="ovr">
        <div>
          <div className="big num" style={{ color: toneVar(overall.avgGrade) }}>
            {fmt1(overall.avgGrade)}
          </div>
          <div className="ol">Run average</div>
        </div>
        <div className="od num">
          {overall.handCount} hands
          <br />
          {overall.decisionCount} decisions
          <br />
          {overall.best && overall.worst ? `best ${overall.best.grade} . worst ${overall.worst.grade}` : ''}
        </div>
      </div>
    </div>
  );
}
